import { useCallback, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { DragEndEvent, DragOverEvent, DragStartEvent, UniqueIdentifier } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import { useDossier } from '../DossierContext';

/**
 * Gestion du drag-and-drop sur l'éditeur de dossier.
 *
 * Consomme le `DossierContext` (pas de props). Les cas couverts :
 *
 *   - réorganisation des chapitres dans la sidebar
 *   - déplacement d'un bloc dans le même chapitre / même section
 *   - déplacement d'un bloc vers un autre chapitre (drop sur l'item
 *     de chapitre dans la sidebar)
 *   - hover prolongé sur un chapitre pendant le drag d'un bloc →
 *     basculement automatique du chapitre actif (debounce 300 ms)
 *
 * Toutes les écritures passent par `updateDossier(draft => …)` ; le
 * hook ne touche jamais directement à l'état du provider.
 */

interface BlockLocation {
  chapterIndex: number;
  sectionIndex: number;
  blockIndex: number;
}

export function useDndKitManagement() {
  const { dossier, updateDossier, currentChapterIndex, setCurrentChapterIndex } = useDossier();

  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);
  const [activeType, setActiveType] = useState<'block' | 'chapter'>('block');
  const [activeItem, setActiveItem] = useState<any>(null);
  const [activeOverId, setActiveOverId] = useState<UniqueIdentifier | null>(null);

  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastHoveredChapterId = useRef<UniqueIdentifier | null>(null);

  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      const { active } = event;
      setActiveId(active.id);
      setActiveOverId(null);

      if (active.data.current?.type === 'chapter') {
        setActiveType('chapter');
        const chapter = dossier?.content.find(c => c._id === active.id);
        setActiveItem(chapter);
      } else {
        setActiveType('block');
        setActiveItem(active.data.current?.block || null);
      }
    },
    [dossier]
  );

  const handleDragOver = useCallback(
    (event: DragOverEvent) => {
      const { active, over } = event;
      setActiveOverId(over?.id || null);

      if (active.data.current?.type !== 'chapter' && over?.data.current?.type === 'chapter') {
        const chapterId = over.id;
        if (lastHoveredChapterId.current !== chapterId) {
          if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
          lastHoveredChapterId.current = chapterId;
          hoverTimeoutRef.current = setTimeout(() => {
            if (dossier?.content) {
              const newIndex = dossier.content.findIndex(c => c._id === chapterId);
              if (newIndex !== undefined && newIndex !== -1 && newIndex !== currentChapterIndex) {
                setCurrentChapterIndex(newIndex);
              }
            }
          }, 300);
        }
      } else {
        if (hoverTimeoutRef.current) {
          clearTimeout(hoverTimeoutRef.current);
          hoverTimeoutRef.current = null;
          lastHoveredChapterId.current = null;
        }
      }
    },
    [dossier, currentChapterIndex, setCurrentChapterIndex]
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      setActiveId(null);
      setActiveItem(null);
      setActiveOverId(null);

      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
        hoverTimeoutRef.current = null;
      }
      lastHoveredChapterId.current = null;

      if (!over || !dossier) return;

      // Réorganisation de chapitres
      if (active.data.current?.type === 'chapter') {
        if (active.id === over.id) return;

        const activeIndex = dossier.content.findIndex(ch => ch._id === active.id);
        const overIndex = dossier.content.findIndex(ch => ch._id === over.id);
        if (activeIndex === -1 || overIndex === -1) return;

        const updatedChapters = arrayMove(dossier.content, activeIndex, overIndex);

        if (activeIndex === currentChapterIndex) {
          setCurrentChapterIndex(overIndex);
        } else if (
          (activeIndex < currentChapterIndex && overIndex >= currentChapterIndex) ||
          (activeIndex > currentChapterIndex && overIndex <= currentChapterIndex)
        ) {
          const currentChapterId = dossier.content[currentChapterIndex]._id;
          const newCurrentIndex = updatedChapters.findIndex(c => c._id === currentChapterId);
          setCurrentChapterIndex(newCurrentIndex);
        }

        updateDossier(draft => {
          draft.content = updatedChapters;
        });
        return;
      }

      // Déplacement de bloc
      let destChapterIndex = currentChapterIndex;
      let destSectionIndex = 0;
      let destBlockIndex = 0;
      let isDropOnChapter = false;

      if (over.data.current?.type === 'chapter') {
        isDropOnChapter = true;
        const droppedChapterIndex = dossier.content.findIndex(c => c._id === over.id);
        if (droppedChapterIndex === -1) return;

        destChapterIndex = droppedChapterIndex;
        const destChapter = dossier.content[destChapterIndex];
        if (destChapter.sections && destChapter.sections.length > 0) {
          destSectionIndex = destChapter.sections.length - 1;
          destBlockIndex = destChapter.sections[destSectionIndex].blocks.length;
        }
      } else {
        const overData = over.data.current as
          | { sectionIndex: number; blockIndex: number }
          | undefined;
        if (!overData) return;
        destSectionIndex = overData.sectionIndex;
        destBlockIndex = overData.blockIndex;
      }

      // Localiser la source par ID
      let source: BlockLocation | null = null;
      outer: for (let cIdx = 0; cIdx < dossier.content.length; cIdx++) {
        const chapter = dossier.content[cIdx];
        if (!chapter.sections) continue;
        for (let sIdx = 0; sIdx < chapter.sections.length; sIdx++) {
          const section = chapter.sections[sIdx];
          if (!section.blocks) continue;
          for (let bIdx = 0; bIdx < section.blocks.length; bIdx++) {
            if (section.blocks[bIdx]._id === active.id) {
              source = { chapterIndex: cIdx, sectionIndex: sIdx, blockIndex: bIdx };
              break outer;
            }
          }
        }
      }

      if (!source) {
        console.error('useDndKitManagement: source block introuvable', active.id);
        return;
      }

      const sourceChapterIndex = source.chapterIndex;
      const sourceSectionIndex = source.sectionIndex;
      const sourceBlockIndex = source.blockIndex;

      updateDossier(draft => {
        const sourceChapter = draft.content[sourceChapterIndex];
        if (!sourceChapter?.sections?.[sourceSectionIndex]?.blocks) return;

        const movedBlock = sourceChapter.sections[sourceSectionIndex].blocks[sourceBlockIndex];
        if (!movedBlock) return;

        sourceChapter.sections[sourceSectionIndex].blocks.splice(sourceBlockIndex, 1);

        const destChapter = draft.content[destChapterIndex];
        if (!destChapter) return;
        if (!destChapter.sections || destChapter.sections.length === 0) {
          destChapter.sections = [{ _id: uuidv4(), type: 'text', blocks: [] } as any];
        }

        const safeDestSectionIndex = Math.min(destSectionIndex, destChapter.sections.length - 1);
        if (safeDestSectionIndex < 0) return;

        const targetSection = destChapter.sections[safeDestSectionIndex];
        if (!targetSection) return;
        if (!targetSection.blocks) targetSection.blocks = [];

        const intraSection =
          sourceChapterIndex === destChapterIndex &&
          sourceSectionIndex === safeDestSectionIndex &&
          !isDropOnChapter;

        if (intraSection) {
          // arrayMove attend que la source soit toujours présente ;
          // on l'a déjà retirée, on la remet temporairement.
          sourceChapter.sections[sourceSectionIndex].blocks.splice(
            sourceBlockIndex,
            0,
            movedBlock
          );
          sourceChapter.sections[sourceSectionIndex].blocks = arrayMove(
            sourceChapter.sections[sourceSectionIndex].blocks,
            sourceBlockIndex,
            destBlockIndex
          );
        } else {
          targetSection.blocks.splice(destBlockIndex, 0, movedBlock);
        }
      });
    },
    [dossier, currentChapterIndex, updateDossier, setCurrentChapterIndex]
  );

  return {
    handleDragStart,
    handleDragOver,
    handleDragEnd,
    activeId,
    activeType,
    activeItem,
    activeOverId,
  };
}

export default useDndKitManagement;
